/* eslint-disable camelcase */
import * as React from "react"
import * as yup from "yup"
import moment from "moment"
import { Appearance, TextInput, ViewStyle } from "react-native"
import { Cycle, Farm } from "../../../database"
import { DateTimePickerField } from "../../field/date-time-picker-field/date-time-picker-field"
import { FormProps } from "../form"
import { Field, Formik } from "formik"
import { ModelField } from "../../field/model-field/model-field"
import { NumeralField } from "../../field/numeral-field/numeral-field"
import { Text } from "../../text/text"
import { TextField } from "../../field/text-field/text-field"
import { View } from "native-base"
import { color } from "../../../theme"
import { observer } from "mobx-react-lite"
import { translate } from "../../../i18n"
import { useCallback, useMemo } from "react"
import { MultipleImageField } from "../../field/multiple-image-field/multiple-image-field"
import { SamplingAverageWeightField } from "../../field/sampling-average-weight-field/sampling-average-weight-field"
// import { useStores } from "../../../models"
import { WalkthroughElement } from "../../walkthrough-element/walkthrough-element"

const TITLE: ViewStyle = {
  borderTopWidth: 1,
  borderColor: color.palette.background,
  paddingHorizontal: 16,
  paddingTop: 16,
  paddingBottom: 10,
}

const BORDER: ViewStyle = {
  backgroundColor: color.palette.background,
  height: 4,
}

export interface SamplingFormProps extends FormProps {
  /**
   * An optional style override useful for padding & margin.
   */
  initialValues?: {
    farm_id?: number
    cycle_id?: number
    average_weight?: number
    sampled_at?: string
    remark?: string
    files?: Array<any>
  }
}

/**
 * Describe your component here
 */
export const SamplingForm = observer(function SamplingForm(props: SamplingFormProps) {
  const {
    initialValues: initialValuesProps = {},
    onSubmit: onSubmitProps,
    colorScheme = Appearance.getColorScheme(),
    innerRef,
    parentScrollViewRef: scrollViewRef,
    ...rest
  } = props
  // const { userStore: { user } } = useStores();
  // const { access_samplino } = user?.state || {};
  const access_samplino = true

  const input = {
    average_weight: React.useRef<TextInput>(),
    remark: React.useRef<TextInput>(),
  }

  const nextInput = {
    average_weight: () => input.remark.current && input.remark.current.focus(),
  }

  const onSubmit = useCallback((values) => {
    onSubmitProps({
      ...values,
      files: values.files.map((f, i) => {
        const initialValueFile = (initialValuesProps.files || [])[i]
        const previousUri = ((initialValuesProps.files || [])[i] || {}).path || null
        const uri = (f || {}).uri || null
        if (uri !== previousUri || (!uri && initialValueFile)) {
          if (uri)
            return {
              ...initialValueFile,
              fileable_type: 'App\\Sampling',
              title: f.fileName,
              type: f.type,
              size: f.fileSize,
              path: uri
            }
          else if (!uri && initialValueFile?.id) // Delete
            return {
              _action: 'delete',
              ...initialValueFile,
            }
        }
        return initialValueFile
      }),
    })
  }, [initialValuesProps.files, onSubmitProps])

  const validationSchema = yup.object({
    farm_id: yup.number().nullable().required().label(translate("samplingForm.farmId")),
    cycle_id: yup.number().nullable().required().label(translate("samplingForm.cycleId")),
    sampled_at: yup.mixed().nullable().required().label(translate("samplingForm.sampledAt")),
    average_weight: yup.number().min(0.1).max(100).nullable().required().label(translate("samplingForm.averageWeight")),
    files: yup.array().label(translate("samplingForm.files")),
    remark: yup.string().ascii().max(255).nullable().label(translate("samplingForm.remark")),
  })

  const initialValues = useMemo<SamplingFormProps["initialValues"]>(() => ({
    farm_id: null,
    ...initialValuesProps,
    files: [null, null, null].map((f, i) => (
      (initialValuesProps.files || [])[i] || {}).path_source || f
    ),
    sampled_at: initialValuesProps.sampled_at || moment().format("YYYY-MM-DD"),
  }), [initialValuesProps])

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      innerRef={innerRef}
      {...rest}
    >
      {({ initialValues, values, setFieldValue, setFieldTouched }) => (
        <>
          <ModelField
            name="farm_id"
            txPrefix="samplingForm"
            colorScheme={colorScheme}
            formControlProps={{ isRequired: true }}
            modelPickerProps={{
              Model: Farm,
              isDisabled: !!initialValues.farm_id,
              onValueChange: async (value) => {
                await setFieldValue("cycle_id", null)
                await setFieldValue("farm_id", value || null)
                await setFieldTouched("farm_id")
              },
            }}
          />
          <View style={TITLE}>
            <Text tx="samplingForm.sampling" preset="title3" colorScheme={colorScheme} />
          </View>
          <View style={BORDER} />
          <WalkthroughElement
            id="sampling-form"
            scrollViewRef={scrollViewRef}
            scrollOffset={250}
          >
            <ModelField
              key={`${values.farm_id}-cycle_id`}
              name="cycle_id"
              txPrefix="samplingForm"
              colorScheme={colorScheme}
              formControlProps={{ isRequired: true }}
              modelPickerProps={{
                Model: Cycle,
                dataLabel: "pond_name",
                fetchData: (model, search) => (
                  model.fetch({
                    farm_id__eq: values.farm_id,
                    sort: 'started_at,desc|pond_name_alpha_numeric'
                  }).then((models) => models.filter((m) => (
                    `${m.pond_name} - ${moment(m.started_at).format("DD MMM YYYY")}${m.finished_at ? ` (${translate("Finished")})` : ""}`
                  ).toLowerCase().includes((search || '').toLowerCase())))
                ),
                renderSelectedItemText: (item) => item && `${item.pond_name} - ${moment(item.started_at).format("DD MMM YYYY")}${item.finished_at ? ` (${translate("Finished")})` : ""}`,
                clearable: true,
                isDisabled: !values.farm_id || !!initialValues.cycle_id,
                titleTx: "samplingForm.cycleIdPlaceholder"
              }}
            />
            <DateTimePickerField
              name="sampled_at"
              txPrefix="samplingForm"
              colorScheme={colorScheme}
              formControlProps={{ isRequired: true }}
            />
            {!access_samplino ? <NumeralField
              name="average_weight"
              txPrefix="samplingForm"
              colorScheme={colorScheme}
              input={input}
              nextInput={nextInput}
              formControlProps={{ isRequired: true }}
              numeralInputProps={{ format: '0,0.[00]' }}
            /> : <SamplingAverageWeightField
              FieldComponent={Field}
              name="average_weight"
              txPrefix="samplingForm"
              colorScheme={colorScheme}
              input={input}
              nextInput={nextInput}
              formControlProps={{ isRequired: true }}
              numeralInputProps={{ format: '0,0.[00]' }}
              samplingAverageWeightInputProps={{
                async onImageProcessed(asset) {
                  const index = values.files.findIndex((f) => !f);
                  await setFieldValue(`files.${index}`, asset)
                },
                onStateProcessed(state) {
                  setFieldValue('state', state)
                }
              }}
            />}
            <MultipleImageField
              name="files"
              txPrefix="samplingForm"
              colorScheme={colorScheme}
            />
            <TextField
              name="remark"
              txPrefix="samplingForm"
              colorScheme={colorScheme}
              input={input}
              nextInput={nextInput}
              formControlProps={{ inputProps: { multiline: true, numberOfLines: 3, onSubmitEditing: undefined } }}
            />
          </WalkthroughElement>
        </>
      )}
    </Formik>
  )
})

SamplingForm.displayName = 'SamplingForm'
